import { TvAssetCollector } from './chart-tv-asset-collector';
import {
  shouldAttachTvAssetTiming,
  tvAssetSourceEnum,
  type TvAssetTimingMetrics,
  type TvAssetTimingSource,
} from './chart-tv-asset-timing';

export type ChartSurface = 'web_trade' | 'web_fullchart' | 'webview_chart' | 'webview_fullchart';

export type ChartLoadTrigger = 'initial' | 'symbol' | 'interval' | 'price_type' | 'tab_return' | 'reconnect';

export type ChartLoadStage =
  | 'shell_visible'
  | 'module_ready'
  | 'storage_migration_started'
  | 'storage_migration_finished'
  | 'saved_interval_resolved'
  | 'widget_created'
  | 'chart_ready'
  | 'symbol_resolve_started'
  | 'symbol_resolved'
  | 'historical_request_started'
  | 'historical_data_received'
  | 'historical_data_ready'
  | 'realtime_fresh'
  | 'overlay_hidden';

export type ChartLoadOutcome = 'success' | 'failed' | 'aborted';

export interface ChartLoadSession {
  id: string;
  surface: ChartSurface;
  createdAt: number;
}

export interface ChartLoadAttemptMetadata {
  symbol?: string;
  resolution?: string;
}

export interface ChartLoadAttemptSummary extends ChartLoadAttemptMetadata {
  chart_session_id: string;
  chart_instance_id: string;
  chart_attempt_id: string;
  chart_surface: ChartSurface;
  chart_trigger: ChartLoadTrigger;
  chart_triggers: ChartLoadTrigger[];
  outcome: ChartLoadOutcome;
  last_stage?: ChartLoadStage;
  reason?: string;
  was_delayed: boolean;
  elapsed_ms: number;
  stage_offsets_ms: Partial<Record<ChartLoadStage, number>>;
  shell_to_module_ms?: number;
  module_to_widget_ms?: number;
  widget_to_chart_ready_ms?: number;
  chart_ready_to_symbol_ms?: number;
  symbol_to_historical_ms?: number;
  historical_to_realtime_ms?: number;
  shell_to_overlay_hidden_ms?: number;
  tv_asset_count?: number;
  tv_asset_cache_hit_count?: number;
  tv_asset_cache_miss_count?: number;
  tv_asset_unknown_count?: number;
  tv_asset_transfer_bytes?: number;
  tv_asset_download_ms?: number;
  tv_widget_init_ms?: number;
  tv_asset_source?: TvAssetTimingMetrics['tv_asset_source'];
}

export interface ChartLoadLogContext extends ChartLoadAttemptMetadata {
  chart_session_id: string;
  chart_instance_id: string;
  chart_attempt_id?: string;
  chart_surface: ChartSurface;
  chart_trigger?: ChartLoadTrigger;
  chart_triggers?: ChartLoadTrigger[];
  last_stage?: ChartLoadStage;
  elapsed_ms?: number;
}

export interface ChartLoadReporter {
  reportAttempt(summary: ChartLoadAttemptSummary): void;
  addTiming(stage: Extract<ChartLoadStage, 'shell_visible' | 'overlay_hidden'>): void;
}

interface Attempt {
  id: string;
  triggers: ChartLoadTrigger[];
  startedAt: number;
  stages: Partial<Record<ChartLoadStage, number>>;
  stageOrder: ChartLoadStage[];
  metadata: ChartLoadAttemptMetadata;
  wasDelayed: boolean;
  finishedAt?: number;
  tvAssetMetrics?: TvAssetTimingMetrics;
}

interface ChartLoadRecorderOptions {
  session: ChartLoadSession;
  reporter: ChartLoadReporter;
  now?: () => number;
  createId?: (prefix: string) => string;
}

let fallbackId = 0;

const createEphemeralId = (prefix: string) => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return `${prefix}-${globalThis.crypto.randomUUID()}`;
  fallbackId += 1;
  return `${prefix}-${Date.now()}-${fallbackId}`;
};

export const createChartLoadSession = (surface: ChartSurface): ChartLoadSession => ({
  id: createEphemeralId('session'),
  surface,
  createdAt: performance.now(),
});

const roundMilliseconds = (value: number) => Math.round(value * 100) / 100;

const durationBetween = (stages: Partial<Record<ChartLoadStage, number>>, from: ChartLoadStage, to: ChartLoadStage) => {
  const fromTime = stages[from];
  const toTime = stages[to];
  if (fromTime == null || toTime == null || toTime < fromTime) return;
  return roundMilliseconds(toTime - fromTime);
};

export class ChartLoadRecorder {
  readonly instanceId: string;

  readonly #session: ChartLoadSession;
  readonly #reporter: ChartLoadReporter;
  readonly #now: () => number;
  readonly #createId: (prefix: string) => string;

  #currentAttempt: Attempt | undefined;
  #lastAttempt: Attempt | undefined;
  #tvAssetCollector: TvAssetCollector | undefined;

  constructor({
    session,
    reporter,
    now = () => performance.now(),
    createId = createEphemeralId,
  }: ChartLoadRecorderOptions) {
    this.#session = session;
    this.#reporter = reporter;
    this.#now = now;
    this.#createId = createId;
    this.instanceId = createId('instance');
  }

  beginAttempt(trigger: ChartLoadTrigger, metadata: ChartLoadAttemptMetadata = {}, startedAt?: number) {
    if (this.#currentAttempt) {
      this.captureTvAssetTiming();
      this.#finish('aborted', 'replaced');
    }

    const attempt: Attempt = {
      id: this.#createId('attempt'),
      triggers: [trigger],
      startedAt: startedAt ?? this.#now(),
      stages: {},
      stageOrder: [],
      metadata,
      wasDelayed: false,
    };
    this.#currentAttempt = attempt;
    return attempt.id;
  }

  mergeTrigger(trigger: ChartLoadTrigger, metadata: ChartLoadAttemptMetadata = {}) {
    if (!this.#currentAttempt) return this.beginAttempt(trigger, metadata);
    if (!this.#currentAttempt.triggers.includes(trigger)) this.#currentAttempt.triggers.push(trigger);
    this.updateMetadata(metadata);
    return this.#currentAttempt.id;
  }

  ensureAttempt(trigger: ChartLoadTrigger, metadata: ChartLoadAttemptMetadata = {}) {
    if (!this.#currentAttempt) return this.beginAttempt(trigger, metadata);
    this.updateMetadata(metadata);
    return this.#currentAttempt.id;
  }

  get activeTrigger() {
    return this.#currentAttempt?.triggers[0];
  }

  updateMetadata(metadata: ChartLoadAttemptMetadata) {
    if (!this.#currentAttempt) return;
    this.#currentAttempt.metadata = { ...this.#currentAttempt.metadata, ...metadata };
  }

  mark(stage: ChartLoadStage) {
    this.markAt(stage, this.#now());
  }

  markAt(stage: ChartLoadStage, timestamp: number) {
    const attempt = this.#currentAttempt;
    if (!attempt || attempt.stages[stage] != null) return;

    attempt.stages[stage] = timestamp;
    attempt.stageOrder.push(stage);

    if (attempt.triggers[0] === 'initial' && (stage === 'shell_visible' || stage === 'overlay_hidden')) {
      this.#safely(() => this.#reporter.addTiming(stage));
    }
  }

  markDelayed() {
    if (!this.#currentAttempt) return this.getLogContext();
    this.#currentAttempt.wasDelayed = true;
    return this.getLogContext();
  }

  startTvAssetCollection(collector: TvAssetCollector = new TvAssetCollector()) {
    this.#safely(() => {
      this.#tvAssetCollector?.stop({
        windowStart: this.#now(),
        windowEnd: this.#now(),
        iframe: 'unavailable',
      });
      this.#tvAssetCollector = collector;
      collector.start();
    });
  }

  captureTvAssetTiming(getIframe?: () => TvAssetTimingSource | 'unavailable') {
    this.#safely(() => {
      const collector = this.#tvAssetCollector;
      if (!collector) return;
      this.#tvAssetCollector = undefined;
      const attempt = this.#currentAttempt;
      const windowStart = attempt?.stages.widget_created ?? this.#now();
      const windowEnd = attempt?.stages.chart_ready ?? this.#now();
      const iframe = getIframe?.() ?? 'unavailable';
      const metrics = collector.stop({ windowStart, windowEnd, iframe });
      if (attempt) attempt.tvAssetMetrics = metrics;
    });
  }

  recordTvAssetTiming(metrics: TvAssetTimingMetrics) {
    if (!this.#currentAttempt) return;
    this.#currentAttempt.tvAssetMetrics = metrics;
  }

  succeed() {
    this.#finish('success');
  }

  fail(reason: string) {
    this.#finish('failed', reason);
  }

  abort(reason: string) {
    this.#finish('aborted', reason);
  }

  dispose(reason: string = 'unmount') {
    this.captureTvAssetTiming();
    if (this.#currentAttempt) this.abort(reason);
  }

  getLogContext(): ChartLoadLogContext {
    const attempt = this.#currentAttempt ?? this.#lastAttempt;
    if (!attempt) {
      return {
        chart_session_id: this.#session.id,
        chart_instance_id: this.instanceId,
        chart_surface: this.#session.surface,
      };
    }

    return {
      chart_session_id: this.#session.id,
      chart_instance_id: this.instanceId,
      chart_attempt_id: attempt.id,
      chart_surface: this.#session.surface,
      chart_trigger: attempt.triggers[0],
      chart_triggers: [...attempt.triggers],
      last_stage: attempt.stageOrder.at(-1),
      elapsed_ms: roundMilliseconds(Math.max(0, (attempt.finishedAt ?? this.#now()) - attempt.startedAt)),
      ...attempt.metadata,
    };
  }

  #finish(outcome: ChartLoadOutcome, reason?: string) {
    const attempt = this.#currentAttempt;
    if (!attempt) return;

    const finishedAt = this.#now();
    const widgetToChartReadyMs = durationBetween(attempt.stages, 'widget_created', 'chart_ready');
    const tvAssetFields = tvAssetSummaryFields(attempt.triggers[0], attempt.tvAssetMetrics, widgetToChartReadyMs);
    const summary: ChartLoadAttemptSummary = {
      chart_session_id: this.#session.id,
      chart_instance_id: this.instanceId,
      chart_attempt_id: attempt.id,
      chart_surface: this.#session.surface,
      chart_trigger: attempt.triggers[0],
      chart_triggers: [...attempt.triggers],
      outcome,
      last_stage: attempt.stageOrder.at(-1),
      reason,
      was_delayed: attempt.wasDelayed,
      elapsed_ms: roundMilliseconds(Math.max(0, finishedAt - attempt.startedAt)),
      stage_offsets_ms: Object.fromEntries(
        attempt.stageOrder.map((stage) => [
          stage,
          roundMilliseconds(Math.max(0, attempt.stages[stage]! - attempt.startedAt)),
        ]),
      ),
      shell_to_module_ms: durationBetween(attempt.stages, 'shell_visible', 'module_ready'),
      module_to_widget_ms: durationBetween(attempt.stages, 'module_ready', 'widget_created'),
      widget_to_chart_ready_ms: widgetToChartReadyMs,
      chart_ready_to_symbol_ms: durationBetween(attempt.stages, 'chart_ready', 'symbol_resolved'),
      symbol_to_historical_ms: durationBetween(attempt.stages, 'symbol_resolved', 'historical_data_ready'),
      historical_to_realtime_ms: durationBetween(attempt.stages, 'historical_data_ready', 'realtime_fresh'),
      shell_to_overlay_hidden_ms: durationBetween(attempt.stages, 'shell_visible', 'overlay_hidden'),
      ...tvAssetFields,
      ...attempt.metadata,
    };

    attempt.finishedAt = finishedAt;
    this.#lastAttempt = attempt;
    this.#currentAttempt = undefined;
    this.#safely(() => this.#reporter.reportAttempt(summary));
  }

  #safely(callback: () => void) {
    try {
      callback();
    } catch {
      // Observability must never interrupt the chart lifecycle.
    }
  }
}

const tvAssetSummaryFields = (
  trigger: ChartLoadTrigger,
  metrics: TvAssetTimingMetrics | undefined,
  widgetToChartReadyMs: number | undefined,
): Partial<ChartLoadAttemptSummary> => {
  if (!metrics || !shouldAttachTvAssetTiming(trigger)) return {};

  const fields: Partial<ChartLoadAttemptSummary> = {
    tv_asset_source: metrics.tv_asset_source,
    tv_asset_count: metrics.tv_asset_count,
    tv_asset_cache_hit_count: metrics.tv_asset_cache_hit_count,
    tv_asset_cache_miss_count: metrics.tv_asset_cache_miss_count,
    tv_asset_unknown_count: metrics.tv_asset_unknown_count,
    tv_asset_transfer_bytes: metrics.tv_asset_transfer_bytes,
    tv_asset_download_ms: metrics.tv_asset_download_ms,
  };

  if (metrics.tv_asset_source !== tvAssetSourceEnum.unavailable && widgetToChartReadyMs != null) {
    fields.tv_widget_init_ms = roundMilliseconds(Math.max(0, widgetToChartReadyMs - metrics.tv_asset_download_ms));
  }

  return fields;
};
